import { ReactElement, useId } from 'react'
import { clientInfo, customComponents } from 'configureBrand'
import { DeviceErrorAlertBanner } from 'ui/atoms/AlertBanner'
import responseCodes from 'const/responseCodes'
import errorMessages from 'helpers/errorMessages'
import { Link } from 'syf-component-library/ui/atoms'
import { Inset, Stack } from 'syf-component-library/ui/spacing'
import i18n from 'lang/i18n'
import { Small } from 'syf-component-library/ui/typography'
import { CSRAlertBannerType } from 'typings/brands'
import { TransmitResponseCodes } from 'ui/organisms/Transmit/typing'
import {
  TransmitAuthSessionExpiredErrorBanner,
  TransmitAuthenticationFailureErrorBanner,
  TransmitInvalidInputErrorBanner,
  TransmitSystemOrApplicationErrorBanner,
  TransmitUserCancelledErrorBanner
} from 'ui/atoms/AlertBanner/TransmitErrorBanners'
import { LoginAlertProps } from './typing'
import {
  AccessDenied,
  AccountLockedBanner,
  AlertText,
  AlertTitle,
  Banner,
  BoldText
} from './subcomponents'

const {
  accessDenied,
  accountLocked,
  accountTypeDenied,
  deviceValidationDenyError,
  invalidAccount,
  invalidCredential,
  invalidSamsAccountType,
  invalidUserID,
  loginUserNotFound,
  maxAttempt,
  oneMoreAttempt,
  onlineAccessDenied,
  passwordMismatch,
  registerAccountLocked,
  userAlreadyRegistered,
  userNotExist,
  userNotFound
} = responseCodes

const { legacySiteUrl } = clientInfo

const { accountAlreadyRegistered } = errorMessages

const { CsrAlertContent } = customComponents

const LoginAlert = ({ errorMessage }: LoginAlertProps): ReactElement => {
  const { assistanceAlertContacts } = clientInfo.customerSupport
  const alertDescriptionId = useId()

  switch (errorMessage) {
    case invalidCredential:
    case invalidUserID:
    case loginUserNotFound:
    case passwordMismatch:
    case userNotExist:
    case userNotFound:
      return (
        <Banner type="error" aria-describedby={alertDescriptionId}>
          <div id={alertDescriptionId}>
            <AlertText>
              {i18n({ login: 'unRecognizedUserIdAndPassword' })}
            </AlertText>
            <AlertText>{i18n({ login: 'loginAlert' })}</AlertText>
          </div>
        </Banner>
      )
    case maxAttempt:
    case oneMoreAttempt:
      return (
        <Banner type="error">
          <AlertText>
            {i18n({ login: 'unRecognizedUserIDAndPasswordMessage' })}
          </AlertText>
          <BoldText>{i18n({ login: 'userIdLockedAlertMessage' })}</BoldText>
          <AlertText>{i18n({ login: 'unLockUserId' })}</AlertText>
        </Banner>
      )
    case onlineAccessDenied:
      return <AccessDenied />
    case accountLocked:
      return <AccountLockedBanner />
    case registerAccountLocked:
      return (
        <Banner type="error" useLockIcon>
          <AlertTitle>{i18n({ login: 'accountLocked' })}</AlertTitle>
          <Stack size="base" />
          <Small>{i18n({ login: 'invalidAuthCode' })}</Small>
          <Stack size="base" />
          <Small isInline>{i18n({ login: 'contact' })}</Small>
          <CsrAlertContent
            i18n={i18n}
            type={CSRAlertBannerType.REGISTRATIONACCOUNTLOCK}
          />
        </Banner>
      )
    case accessDenied:
      return <AccessDenied />
    case userAlreadyRegistered:
      return <Banner type="error">{accountAlreadyRegistered}</Banner>
    case invalidAccount:
      return (
        <Banner type="error">
          {i18n({ login: 'problemWithFormSubmitAgain' })}
        </Banner>
      )
    case accountTypeDenied:
      return (
        <Banner type="error">
          <BoldText>{i18n({ login: 'accountTypeNotSupported' })}</BoldText>
          <AlertText>
            {i18n({ login: 'click' })}{' '}
            <Link isInline href={legacySiteUrl}>
              {i18n({ link: 'here' })}
            </Link>{' '}
            {i18n({ login: 'returnBackToLegacyUrl' })}
          </AlertText>
        </Banner>
      )
    case invalidSamsAccountType:
      return (
        <Banner type="error" scrollToOnMount>
          <BoldText>{i18n({ login: 'accountTypeNotSupported' })}</BoldText>
          <AlertText>
            {i18n({ login: 'samsAccountNotSupported' })}{' '}
            <Link
              isInline
              target="_blank"
              rel="noopener noreferrer"
              href={legacySiteUrl}
            >
              {i18n({ link: 'here' })}
            </Link>{' '}
            {i18n({ login: 'toLogin' })}
          </AlertText>
        </Banner>
      )
    case deviceValidationDenyError:
      return (
        <Inset bottom="small" top="base">
          <DeviceErrorAlertBanner i18n={i18n} />
        </Inset>
      )
    // Transmit - 0001 / 0007 / 0017 / 0418
    case TransmitResponseCodes.SystemError:
    case TransmitResponseCodes.JWTVerificationError:
    case TransmitResponseCodes.JavaScriptError:
    case TransmitResponseCodes.AkamaiError:
      return <TransmitSystemOrApplicationErrorBanner i18n={i18n} />
    // Transmit - 0003
    case TransmitResponseCodes.InvalidInputData:
      return (
        <TransmitInvalidInputErrorBanner
          i18n={i18n}
          assistanceAlertContacts={assistanceAlertContacts}
        />
      )
    // Transmit - 0004
    case TransmitResponseCodes.UserCancelled:
      return <TransmitUserCancelledErrorBanner i18n={i18n} />
    // Transmit - 0005
    case TransmitResponseCodes.AuthenticationFailure:
      return <TransmitAuthenticationFailureErrorBanner i18n={i18n} />
    // Transmit - 0006
    case TransmitResponseCodes.SessionExpired:
      return <TransmitAuthSessionExpiredErrorBanner i18n={i18n} />
    default:
      return <div />
  }
}

export default LoginAlert
